import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';

const Donate = () => {
  const { register, handleSubmit, formState: { errors } } = useForm();
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [loading, setLoading] = useState(false);

  const amounts = [
    { value: 500, label: 'Feed a family' },
    { value: 1000, label: 'Education kit' },
    { value: 2500, label: 'Medical support' },
    { value: 5000, label: 'Community program' }
  ];

  const loadRazorpay = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const onSubmit = async (data) => {
    const amount = data.customAmount || selectedAmount;
    if (!amount || amount < 100) {
      toast.error('Please select or enter an amount (minimum ₹100)');
      return;
    }

    setLoading(true);
    try {
      const orderResponse = await axios.post(
        `${import.meta.env.VITE_API_URL}/donations/create-order`,
        {
          amount: Number(amount),
          name: data.name,
          email: data.email
        }
      );

      const isLoaded = await loadRazorpay();
      if (!isLoaded) {
        toast.error('Failed to load payment gateway');
        setLoading(false);
        return;
      }

      const options = {
        key: import.meta.env.VITE_RAZORPAY_KEY_ID,
        amount: orderResponse.data.amount,
        currency: 'INR',
        name: 'Sevasaredham',
        description: 'Donation',
        order_id: orderResponse.data.orderId,
        handler: async (response) => {
          try {
            await axios.post(`${import.meta.env.VITE_API_URL}/donations/verify`, {
              ...response,
              ...data,
              amount: Number(amount)
            });
            toast.success('Thank you for your donation! 🙏');
          } catch (error) {
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          name: data.name,
          email: data.email,
          contact: data.phone
        },
        theme: {
          color: '#2C5F2D'
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      toast.error('Payment initiation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="section-padding bg-gray-50">
      <div className="container-custom max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Make a Donation</h1>
          <p className="text-xl text-gray-600">
            Your contribution helps us continue our mission
          </p>
        </motion.div>

        <div className="card p-8">
          <h3 className="text-2xl font-bold mb-6">Choose Donation Amount</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {amounts.map(({ value, label }) => (
              <motion.button
                key={value}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setSelectedAmount(value)}
                className={`p-6 border-2 rounded-lg text-center transition-all ${
                  selectedAmount === value
                    ? 'border-primary bg-primary/10'
                    : 'border-gray-300 hover:border-primary'
                }`}
              >
                <div className="text-3xl font-bold text-primary mb-1">₹{value}</div>
                <div className="text-sm text-gray-600">{label}</div>
              </motion.button>
            ))}
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div>
              <label className="block font-semibold mb-2">Or Enter Custom Amount</label>
              <input
                type="number"
                min="100"
                {...register('customAmount')}
                className="input"
                placeholder="Enter amount (₹)"
                onChange={(e) => e.target.value && setSelectedAmount(null)}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block font-semibold mb-2">Full Name *</label>
                <input
                  {...register('name', { required: 'Name is required' })}
                  className="input"
                />
                {errors.name && <span className="text-red-500 text-sm">{errors.name.message}</span>}
              </div>
              <div>
                <label className="block font-semibold mb-2">Email *</label>
                <input
                  type="email"
                  {...register('email', { required: 'Email is required' })}
                  className="input"
                />
                {errors.email && <span className="text-red-500 text-sm">{errors.email.message}</span>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block font-semibold mb-2">Phone *</label>
                <input
                  {...register('phone', { required: 'Phone is required' })}
                  className="input"
                />
                {errors.phone && <span className="text-red-500 text-sm">{errors.phone.message}</span>}
              </div>
              <div>
                <label className="block font-semibold mb-2">PAN (for 80G certificate)</label>
                <input {...register('pan')} className="input" placeholder="ABCDE1234F" />
              </div>
            </div>

            <div>
              <label className="block font-semibold mb-2">Message (Optional)</label>
              <textarea
                {...register('message')}
                className="input"
                rows="3"
                placeholder="Leave a message of hope..."
              ></textarea>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn btn-secondary w-full text-lg"
            >
              {loading ? 'Processing...' : 'Proceed to Payment 🔒'}
            </button>
            <p className="text-center text-sm text-gray-500">
              Secure payment • 80G Tax exemption available
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Donate;
